import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Paper, Menu, MenuItem, ListItemIcon, ListItemText, Divider, useTheme } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import LogoutIcon from '@mui/icons-material/Logout'
import { ApiDataWrapperContext } from '../contexts/ApiDataWrapper'
import { AuthContext } from '../contexts/Auth'
import LinkWithQuery from '../components/LinkWithQuery'
import Text from '../components/Text'
import Logo from '../components/Logo'
import ConnectionChip from './ConnectionChip'
import { itensDoTopo, itensDoMais, itemEstaAtivo } from './navItems'

// A faixa do topo. Acima de 1024px ela mostra as abas; abaixo disso sobram só
// a marca e o chip de conexão, porque a navegação passa para a barra inferior.
// O corte é único — não existe estado intermediário.

function Aba({ item, ativo }) {
  const theme = useTheme()
  const Icone = item.icon

  return (
    <LinkWithQuery
      to={item.to}
      className="foco-navegacao"
      style={{ textDecoration: 'none' }}
      aria-current={ativo ? 'page' : undefined}
    >
      <div
        className="flex items-center gap-2 h-[59px] px-3 whitespace-nowrap transition-colors"
        style={{
          borderBottom: `3px solid ${ativo ? theme.palette.primary.main : 'transparent'}`,
          color: ativo ? theme.palette.primary.main : theme.palette.text.secondary
        }}
      >
        {/* O ícone some entre 1024 e 1280px para as abas caberem sem apertar. */}
        <Icone style={{ fontSize: 18 }} className="hidden xl:block" />
        <span className="text-sm" style={{ fontWeight: ativo ? 600 : 400 }}>
          {item.label}
        </span>
      </div>
    </LinkWithQuery>
  )
}

export default function TopNav() {
  const theme = useTheme()
  const location = useLocation()
  const { user } = useContext(ApiDataWrapperContext)
  const { logout } = useContext(AuthContext)
  const [anchorEl, setAnchorEl] = useState(null)

  const abas = itensDoTopo(user)
  const noMais = itensDoMais(user)
  const menuAberto = Boolean(anchorEl)
  const fecharMenu = () => setAnchorEl(null)

  // Se a página aberta mora dentro do "Mais", o botão fica marcado — senão o
  // cliente entra em Configurações e o topo inteiro parece apagado.
  const algumDoMaisEstaAtivo = noMais.some(item => itemEstaAtivo(item, location.pathname))
  const maisDestacado = menuAberto || algumDoMaisEstaAtivo

  return (
    <Paper
      square
      elevation={0}
      component="header"
      className="sticky top-0 z-[900]"
      style={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        '--cor-foco': theme.palette.primary.main
      }}
    >
      <div className="flex items-center gap-4 h-[60px] px-4">
        <LinkWithQuery
          to="/"
          className="foco-navegacao flex-none"
          style={{ textDecoration: 'none' }}
          aria-label="Ir para o início"
        >
          <Logo tamanho="topo" />
        </LinkWithQuery>

        <nav
          className="hidden lg:flex items-center flex-1 min-w-0 h-full overflow-x-auto"
          aria-label="Navegação principal"
        >
          {abas.map(item => (
            <Aba key={item.to} item={item} ativo={itemEstaAtivo(item, location.pathname)} />
          ))}

          <button
            onClick={e => setAnchorEl(e.currentTarget)}
            aria-expanded={menuAberto}
            aria-haspopup="menu"
            className="foco-navegacao flex items-center gap-2 h-[59px] px-3 ml-auto whitespace-nowrap"
            style={{
              color: maisDestacado ? theme.palette.primary.main : theme.palette.text.secondary,
              borderBottom: `3px solid ${algumDoMaisEstaAtivo ? theme.palette.primary.main : 'transparent'}`,
              fontWeight: algumDoMaisEstaAtivo ? 600 : 400
            }}
          >
            <MoreHorizIcon style={{ fontSize: 18 }} />
            <span className="text-sm">Mais</span>
          </button>
        </nav>

        {/* No celular a marca e o chip dividem a faixa sozinhos. */}
        <div className="ml-auto lg:ml-0">
          <ConnectionChip />
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={menuAberto}
        onClose={fecharMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 2 } } }}
      >
        {noMais.map(item => {
          const Icone = item.icon
          return (
            <MenuItem
              key={item.to}
              component={LinkWithQuery}
              to={item.to}
              onClick={fecharMenu}
              selected={itemEstaAtivo(item, location.pathname)}
            >
              <ListItemIcon><Icone fontSize="small" /></ListItemIcon>
              <ListItemText>
                <div className="flex items-center gap-2">
                  <Text className="text-sm">{item.label}</Text>
                  {item.badge && (
                    <span
                      className="font-black text-[10px]"
                      style={{ color: theme.palette.primary.main }}
                    >
                      {item.badge}
                    </span>
                  )}
                </div>
              </ListItemText>
            </MenuItem>
          )
        })}

        <Divider />

        <MenuItem onClick={() => { fecharMenu(); logout() }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText><Text className="text-sm">Sair da conta</Text></ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  )
}
